/**
 * CHOOSE THE ACCOUNT FROM THE CLIENT'S CHART — OR CREATE IT HERE (a tester's report, 2026-10-21: "whenever Leveri needs
 * the user to identify an account, it should provide the client's Chart of Accounts for selection and allow a missing
 * account to be created directly within the same workflow").
 *
 * The chart and the types arrive from the server (`work.preparation.chart`, `accountTypes`); this file searches them by
 * code, name or type, and sends one act. Whether the account exists, whether a new name duplicates one, and whether its
 * type takes money the lines' way are the server's to decide — it refuses in words and nothing is filed. The review
 * controls (Accept / Adjust / Reject / Request evidence) stay beside this; they are not how an account is chosen.
 */
import { useMemo, useState } from "react";
import { WorkActionResponse, type PreparationView, type WorkFile } from "@leveri/contracts";
import { Button, Notice, Reading, Select, TextField } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";
import { useKeyring } from "./keys.js";

export type AccountTarget = { kind: "PATTERN"; key: string } | { kind: "HEAD"; name: string };

const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

export function AccountPicker({ workId, target, about, chart, accountTypes, label = "Post to an account", onWork, onOutcome }: {
  workId: string;
  target: AccountTarget;
  /** What is being posted, in words ("2 lines of SHELL FUEL", "the client's head “Fuel”"). */
  about: string;
  chart: PreparationView["chart"];
  accountTypes: PreparationView["accountTypes"];
  label?: string;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [find, setFind] = useState("");
  const [chosen, setChosen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const keyFor = useKeyring();

  // A head is never posted to itself.
  const choices = useMemo(() => chart.filter((a) => !(target.kind === "HEAD" && same(a.name, target.name))), [chart, target]);
  const shown = useMemo(() => {
    const words = find.trim().toLowerCase();
    if (!words) return choices;
    return choices.filter((a) => a.key === chosen || [a.code ?? "", a.name, a.typeLabel].some((w) => w.toLowerCase().includes(words)));
  }, [choices, find, chosen]);
  const twin = creating && newName.trim() ? chart.find((a) => same(a.name, newName)) ?? null : null;
  const ready = creating ? newName.trim().length > 0 && newType !== "" && !twin : chosen !== null;

  function startCreating() {
    setCreating(true); setChosen(null); setFailure(null);
    // What was searched for and not found is the likely name of the account to create.
    if (!newName && find.trim() && shown.length === 0) setNewName(find.trim());
  }

  async function send() {
    if (busy || !ready) return;
    setBusy(true); setFailure(null);
    try {
      const account = creating ? { create: { name: newName.trim(), classKey: newType } } : { key: chosen! };
      const result = await call("POST", `/api/work/${workId}/accounts/choose`, WorkActionResponse, {
        target, account,
        // The same choice pressed twice is one act; another account is another act.
        submissionKey: keyFor(`account:${JSON.stringify(target)}:${JSON.stringify(account)}`.slice(0, 120)),
      });
      onOutcome(result.outcome);
      onWork(result.work);
      setOpen(false);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="mt-1 block text-[12px] font-medium text-forest hover:underline" onClick={() => setOpen(true)} data-testid="account-pick">
        {label}
      </button>
    );
  }
  return (
    <div className="mt-2 border-t border-rule-soft pt-2" data-testid="account-picker">
      <p className="text-[12px] text-stone">Post {about} to an account of the client&rsquo;s chart, or create the account if it is missing.</p>
      {!creating && (
        <>
          <div className="mt-2">
            <TextField label="Find an account" placeholder="Code, name or type" value={find} disabled={busy}
              onChange={(e) => setFind(e.target.value)} data-testid="account-find" />
          </div>
          {choices.length === 0 ? (
            <p className="mt-2 text-[12px] text-stone">The client&rsquo;s chart has no accounts in this work yet.</p>
          ) : shown.length === 0 ? (
            <p className="mt-2 text-[12px] text-stone">No account matches &ldquo;{find.trim()}&rdquo;.</p>
          ) : (
            <ul role="listbox" aria-label="The client's chart of accounts" className="mt-2 max-h-[240px] overflow-y-auto border border-rule" data-testid="account-list">
              {shown.map((a) => (
                <li key={a.key} role="option" aria-selected={chosen === a.key}>
                  <button type="button" disabled={busy} onClick={() => setChosen(a.key)}
                    className={`grid w-full grid-cols-[72px_minmax(0,1fr)_auto] gap-3 border-b border-rule-soft px-3 py-2 text-left text-[13px] last:border-b-0 ${chosen === a.key ? "bg-forest-tint text-ink" : "text-ink hover:bg-raised"}`}>
                    <span className="text-stone [font-variant-numeric:tabular-nums]">{a.code ?? "—"}</span>
                    <span className="min-w-0 truncate">{a.name}</span>
                    <span className="text-[12px] text-stone">{a.typeLabel}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button type="button" className="mt-2 text-[12px] font-medium text-forest hover:underline" disabled={busy} onClick={startCreating} data-testid="account-create-open">
            {find.trim() && shown.length === 0 ? `Create “${find.trim()}” as a new account` : "Create a new account"}
          </button>
        </>
      )}
      {creating && (
        <div className="mt-2 grid gap-2 sm:grid-cols-2" data-testid="account-create">
          <TextField label="Account name" value={newName} disabled={busy} onChange={(e) => setNewName(e.target.value)} data-testid="account-new-name" />
          <Select label="Type" value={newType} disabled={busy} onChange={(e) => setNewType(e.target.value)} data-testid="account-new-type">
            <option value="">Choose a type</option>
            {accountTypes.map((t) => <option key={t.classKey} value={t.classKey}>{t.label}</option>)}
          </Select>
          {twin && (
            <p className="text-[12px] text-att-text sm:col-span-2">
              &ldquo;{twin.name}&rdquo;{twin.code ? ` (${twin.code})` : ""} is already in the chart.{" "}
              <button type="button" className="font-medium text-forest hover:underline" onClick={() => { setCreating(false); setChosen(twin.key); setFind(twin.name); }}>Choose it</button>
            </p>
          )}
          <button type="button" className="justify-self-start text-[12px] text-stone hover:underline sm:col-span-2" disabled={busy} onClick={() => setCreating(false)}>
            Back to the chart
          </button>
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button size="sm" disabled={busy || !ready} onClick={() => void send()} data-testid="account-send">
          {creating ? "Create it and post here" : "Post to this account"}
        </Button>
        <button type="button" className="text-[12px] text-stone hover:underline" disabled={busy} onClick={() => { setOpen(false); setFailure(null); }}>Cancel</button>
      </div>
      {busy && <div className="mt-2"><Reading>Recording your choice…</Reading></div>}
      {failure && <div className="mt-2"><Notice>{failure}</Notice></div>}
    </div>
  );
}
